import * as React from 'react';
import { Button, Modal, UIHeading1, ITagOption } from '@cian/ui-kit';
import { useDispatch, useSelector } from 'react-redux';

import { MoreFiltersModalContentLayout } from '../../../components/Filters';
import { FiltersFooterLayout } from '../../../components/Filters/FiltersFooterLayout';
import { SearchButton } from '../../../components/SearchButton';
import { ADFOX_TIMEOUT } from '../../../constants/adfox';
import { IStateTag } from '../../../actions/filterTags';
import { MoreFiltersModalContent } from './MoreFiltersModalContent';
import { definitions } from '../../../actions/filterTags/definitions';
import { getTags } from '../../../selectors/filters';
import { getOffersCount } from '../../../actions/offersData';
import { TTypedThunkDispatch } from '../../../types/thunk';
import { getTagIcon } from '../../FilterTags/getTagIcon';
import { IconActionFilters16 } from '@cian/ui-kit-design-tokens/icons';
import { IApplicationState } from '../../../types/redux';
import { IJsonQuery } from '../../../types/interfaces';

export interface IModalStoreProps {
  count: number | null;
  isCountFetching?: boolean;
  hasUndreground: boolean;
  discountMortgageEnabled: boolean;
  promoSearchTypesLoaded: boolean;
}

export interface IModalDispatchProps {
  onSubmit(): void;
  trackingPopupMoreParamOpen(): void;
  updateTags(): void;
  onClearFilters(): void;
  onResetFilters(jsonQuery: IJsonQuery): void;
}

type TModalProps = IModalStoreProps & IModalDispatchProps;

export const MoreFiltersModalComponent: React.FC<TModalProps> = React.memo(
  ({
    count,
    isCountFetching,
    hasUndreground,
    discountMortgageEnabled,
    promoSearchTypesLoaded,
    updateTags,
    onSubmit,
    trackingPopupMoreParamOpen,
    onClearFilters,
    onResetFilters,
  }) => {
    const [isOpen, setOpen] = React.useState(false);
    const dispatch = useDispatch<TTypedThunkDispatch>();
    const tags = useSelector(getTags);
    const jsonQuery = useSelector((state: IApplicationState) => state.jsonQuery);
    const savedJsonQueryRef = React.useRef<IJsonQuery | null>(null);

    const [bannerLoaded, setBannerLoaded] = React.useState(false);
    const [bannerError, setBannerError] = React.useState(false);

    const adTimer = React.useRef<number>();

    React.useEffect(
      () => () => {
        if (adTimer.current) {
          window.clearTimeout(adTimer.current);
        }
      },
      [adTimer],
    );

    const handleAdfoxMount = React.useCallback(() => {
      if (adTimer.current) {
        window.clearTimeout(adTimer.current);
      }

      setBannerLoaded(true);
      setBannerError(false);
    }, [adTimer]);

    const handleAdfoxError = React.useCallback(() => {
      setBannerLoaded(true);
      setBannerError(true);
    }, []);

    const handleToggle = React.useCallback(
      (isOpen: boolean) => {
        if (isOpen) {
          // Сохраняем текущее состояние фильтров при открытии
          savedJsonQueryRef.current = JSON.parse(JSON.stringify(jsonQuery));
          trackingPopupMoreParamOpen();

          adTimer.current = window.setTimeout(handleAdfoxError, ADFOX_TIMEOUT);
        } else {
          // При закрытии без применения восстанавливаем сохраненное состояние
          if (savedJsonQueryRef.current) {
            onResetFilters(savedJsonQueryRef.current);
            savedJsonQueryRef.current = null;
          }
          updateTags(); // При закрытии модального окна обновляем тэги
        }

        setOpen(isOpen);
        setBannerLoaded(false);
        setBannerError(false);
      },
      [adTimer, handleAdfoxError, trackingPopupMoreParamOpen, updateTags, jsonQuery, onResetFilters],
    );

    const handleSubmit = React.useCallback(() => {
      // При применении фильтров сбрасываем сохраненное состояние
      savedJsonQueryRef.current = null;
      onSubmit();

      setOpen(false);
      setBannerLoaded(false);
      setBannerError(false);
    }, [onSubmit]);

    const handleClose = React.useCallback(() => {
      handleToggle(false);
    }, [handleToggle]);

    const handleClear = React.useCallback(() => {
      onClearFilters();
    }, [onClearFilters]);

    const handleTagDelete = React.useCallback(
      (option: ITagOption) => {
        const tag = tags.find(tag => JSON.stringify(tag.value) + '/' + tag.label === option.value) as IStateTag;
        const definition = definitions.get(tag.keyName);

        if (definition) {
          dispatch(definition.onDeleteAction(tag.value));
        }
      },
      [dispatch, tags],
    );

    const handleClearAllTags = React.useCallback(() => {
      tags.forEach(tag => {
        const definition = definitions.get(tag.keyName);
        if (definition) {
          const onDeleteAction = definition.onDeleteAction(tag.value, { skipUpdateOffersCount: true });
          if (onDeleteAction) {
            dispatch(onDeleteAction);
          }
        }
      });
      dispatch(getOffersCount());
      updateTags();
    }, [dispatch, tags, updateTags]);

    return (
      <>
        <Button
          size="M"
          tabIndex={-1}
          theme="fill_secondary"
          type="button"
          onClick={() => handleToggle(true)}
          afterIcon={<IconActionFilters16 color="current_color" />}
          data-testid="OpenMoreFiltersModalButton"
        />
        <Modal
          size="L"
          open={Boolean(isOpen)}
          onOpenChange={handleClose}
          header={<UIHeading1>Все фильтры</UIHeading1>}
          footer={
            <FiltersFooterLayout>
              <Button
                size="M"
                theme="fill_secondary"
                type="button"
                onClick={handleClear}
                data-testid="ClearFiltersButton"
              >
                Сбросить всё
              </Button>
              <SearchButton count={count} loading={isCountFetching} size="M" onClick={handleSubmit} />
            </FiltersFooterLayout>
          }
          body={
            <MoreFiltersModalContentLayout>
              <MoreFiltersModalContent
                bannerLoaded={bannerLoaded}
                bannerError={bannerError}
                handleAdfoxError={handleAdfoxError}
                handleAdfoxMount={handleAdfoxMount}
                tags={tags}
                handleTagDelete={handleTagDelete}
                handleClearAllTags={handleClearAllTags}
                getTagIcon={getTagIcon}
                hasUndreground={hasUndreground}
                promoSearchTypesLoaded={promoSearchTypesLoaded}
                discountMortgageEnabled={discountMortgageEnabled}
              />
            </MoreFiltersModalContentLayout>
          }
        />
      </>
    );
  },
);

MoreFiltersModalComponent.displayName = 'MoreFiltersModalComponent';
